/**
 * Enhanced Step Definitions for StudentDashboard
 * Uses the new consolidated STEP_DEFINITIONS structure for simplified step building
 * 
 * This module provides the core step building logic that:
 * - Uses currentStep values directly from backend (no more APPLICATION_TYPES)
 * - Accesses step data via STEP_DEFINITIONS[currentStep] pattern
 * - Creates step objects with pre-processed content and actions
 * - Maintains backward compatibility with existing UI behavior
 */

import {
  STEP_STATUS,
  CURRENT_STEP_VALUES
} from './constants';
import {
  getStepDefinition,
  processStepContent
} from './stepHelpers';
import { isLegacyScholarsEnabled } from '../constants/externalConfig';

/**
 * Check if application data represents a "no applications" scenario
 * @param {Object} applicationData - Parsed application data
 * @returns {boolean} True if no applications found
 */
export const isNoApplicationsScenario = (applicationData) => {
  if (!applicationData?.selfService?.currentStep) {
    return true;
  }
  return false;
};

/**
 * Simplified step creation helper function
 * Creates a step object using STEP_DEFINITIONS structure
 * Only sets structural properties (isActive/status), never content overrides
 * @param {string} stepId - Unique step identifier
 * @param {string} currentStep - Current step from backend (used for content lookup)
 * @param {Object} applicationData - Application data for processing
 * @param {Object} overrides - Optional overrides for step properties (structural only)
 * @returns {Object} Step object
 */
const createStep = (stepId, currentStep, applicationData, overrides = {}) => {
  const definition = getStepDefinition(currentStep);
  const processedContent = processStepContent(currentStep, applicationData);
  const { title, ..._overrides } = overrides

  return {
    id: stepId,
    title: title || processedContent?.title || 'Step',
    status: overrides.status || (overrides.isActive ? STEP_STATUS.IN_PROGRESS : STEP_STATUS.COMPLETE),
    isActive: overrides.isActive || false,
    content: processedContent,
    actions: definition?.actions || [],
    templateType: definition?.templateType || 'default',
    templateProps: {
      stepId,
      currentStep,
      ..._overrides
    }
  };
};

/**
 * Enhanced buildStepsFromData function using new STEP_DEFINITIONS structure
 * Simplified approach driven directly by currentStep values
 * @param {Object} applicationData - Application data
 * @returns {Array} Array of step objects
 */
const buildStepsFromData = (applicationData) => {
  const steps = [];
  const { currentStep } = applicationData?.selfService || {};
  const { HONORS_INPROGRESS, HONORS_OFFER, HONORS_DENY, HONORS_DECLINED, HONORS_DECISION_PENDING,
     HONORS_WITHDRAWN, HONORS_COMPLETE, SCHOLARS_PREFERENCES, SCHOLARS_PREFERENCES_SUBMITTED,
     SCHOLARS_AWAITING, SCHOLARS_INPROGRESS, SCHOLARS_NATIVE_PREFERENCES, SCHOLARS_NATIVE_SUBMITTED,
     SCHOLARS_ADMITTED, SCHOLARS_DENIED } = CURRENT_STEP_VALUES;

  // Direct mapping from currentStep to step creation using STEP_DEFINITIONS
  switch (currentStep) {
    case HONORS_INPROGRESS:
      steps.push(createStep('honors-review', currentStep, applicationData, { isActive: true }));
      steps.push(createStep('honors-decision-pending', HONORS_DECISION_PENDING, applicationData, {
        status: STEP_STATUS.PENDING
      }));
      break;

    case HONORS_OFFER:
      steps.push(createStep('honors-review', HONORS_INPROGRESS, applicationData, { isActive: false }));
      steps.push(createStep('honors-decision', currentStep, applicationData, { isActive: true }));
      break;

    case HONORS_DENY:
      steps.push(createStep('honors-review', HONORS_INPROGRESS, applicationData, { isActive: false }));
      steps.push(createStep('honors-decision', currentStep, applicationData, {
        status: STEP_STATUS.COMPLETE,
        isActive: true
      }));
      break;

    case HONORS_DECLINED:
      steps.push(createStep('honors-review', HONORS_INPROGRESS, applicationData, { isActive: false }));
      steps.push(createStep('honors-decision', HONORS_OFFER, applicationData, { isActive: false }));
      steps.push(createStep('application-declined', currentStep, applicationData, {
        isActive: true,
        status: STEP_STATUS.COMPLETE
      }));
      break;

    case HONORS_WITHDRAWN:
      steps.push(createStep('honors-review', HONORS_INPROGRESS, applicationData, { isActive: false }));
      // Show application withdrawn step
      steps.push(createStep('application-withdrawn', currentStep, applicationData, {
        isActive: true,
        status: STEP_STATUS.COMPLETE
      }));
      break;

    case SCHOLARS_PREFERENCES:
      steps.push(createStep('honors-complete', HONORS_COMPLETE, applicationData, { status: STEP_STATUS.COMPLETE }));
      steps.push(createStep('scholars-invitation', currentStep, applicationData, { isActive: true }));
      if (!isLegacyScholarsEnabled()) {
        steps.push(createStep('preferences', currentStep, applicationData, { status: STEP_STATUS.PENDING }));
      }
      break;

    case SCHOLARS_PREFERENCES_SUBMITTED:
      steps.push(createStep('honors-complete', HONORS_COMPLETE, applicationData, {
        status: STEP_STATUS.COMPLETE
      }));
      steps.push(createStep('scholars-invitation', SCHOLARS_PREFERENCES, applicationData, {
        status: STEP_STATUS.COMPLETE,
        title: `Scholars Preferences Complete`
      }));
      steps.push(createStep('scholars-review', currentStep, applicationData, {
        isActive: true,
        status: STEP_STATUS.EXTERNAL_COMPLETE
      }));
      break;

    // =========================================================================
    // Scholars-only flows (native scholars — no honors application)
    // =========================================================================
    case SCHOLARS_AWAITING:
      // Waiting for admin to trigger preferencing — student cannot act
      steps.push(createStep('scholars-received', currentStep, applicationData, { isActive: true }));
      steps.push(createStep('scholars-preferences-pending', SCHOLARS_NATIVE_PREFERENCES, applicationData, {
        status: STEP_STATUS.PENDING
      }));
      break;

    case SCHOLARS_NATIVE_PREFERENCES:
      // Admin triggered preferencing — student can submit preferences
      steps.push(createStep('scholars-received', SCHOLARS_AWAITING, applicationData, { status: STEP_STATUS.COMPLETE }));
      steps.push(createStep('scholars-select-preferences', currentStep, applicationData, { isActive: true }));
      if (!isLegacyScholarsEnabled()) {
        steps.push(createStep('preferences', currentStep, applicationData, { status: STEP_STATUS.PENDING }));
      }
      break;

    case SCHOLARS_NATIVE_SUBMITTED:
      // Native scholars student submitted preferences — under review
      steps.push(createStep('scholars-received', SCHOLARS_AWAITING, applicationData, { status: STEP_STATUS.COMPLETE }));
      steps.push(createStep('scholars-select-preferences', SCHOLARS_NATIVE_PREFERENCES, applicationData, {
        status: STEP_STATUS.COMPLETE,
        title: 'Scholars Preferences Complete'
      }));
      steps.push(createStep('scholars-review', currentStep, applicationData, {
        isActive: true,
        status: STEP_STATUS.EXTERNAL_COMPLETE
      }));
      break;

    case SCHOLARS_INPROGRESS:
      // Catch-all scholars in progress
      steps.push(createStep('scholars-status', currentStep, applicationData, { isActive: true }));
      break;

    // =========================================================================
    // Scholars published decision (set once the outcome is published to PeopleSoft).
    // Mirrors the Honors HONORS_OFFER / HONORS_DENY decision steps.
    // =========================================================================
    case SCHOLARS_ADMITTED:
      steps.push(createStep('scholars-review', SCHOLARS_NATIVE_SUBMITTED, applicationData, { status: STEP_STATUS.COMPLETE }));
      steps.push(createStep('scholars-decision', currentStep, applicationData, { isActive: true }));
      break;

    case SCHOLARS_DENIED:
      steps.push(createStep('scholars-review', SCHOLARS_NATIVE_SUBMITTED, applicationData, { status: STEP_STATUS.COMPLETE }));
      steps.push(createStep('scholars-decision', currentStep, applicationData, {
        status: STEP_STATUS.COMPLETE,
        isActive: true
      }));
      break;

    default:
      // Handle unknown currentStep values gracefully
      console.warn(`Unknown currentStep: ${currentStep}`);
      break;
  }

  return steps;
};

/**
 * Get timeline steps based on application data
 * Uses simplified step building with direct STEP_DEFINITIONS access
 * @param {Object} applicationData - Parsed application data
 * @returns {Array} Array of step objects
 */
export const getTimelineSteps = (applicationData) => {
  // Check for no applications scenario
  if (isNoApplicationsScenario(applicationData)) {
    return [];
  }

  // Use simplified data-driven approach with direct STEP_DEFINITIONS access
  return buildStepsFromData(applicationData);
};